import { computeHash } from './computeHash';
import type { DatabaseRepository } from './repository';

export interface HashRecord {
  id: string;
  tableName: string;
  hash: string;
}

// Input types for blob hashing (kept for file content verification)
export interface BlobInput {
  id: string;
  name: string;
  content: string; // base64 encoded file content
  createdAt: Date | number;
}

/**
 * HashCollector - collects hash records during write operations
 *
 * Handles:
 * - Post snapshots (comprehensive snapshots of posts with all related data)
 * - Blobs (file content verification, separate from post snapshots)
 *
 * Design: Snapshots are created AFTER every operation (create, edit).
 * Each snapshot gets its own createdAt timestamp,
 * which is used for hashing. This preserves edit timing for verification.
 *
 * Usage:
 *   const collector = new HashCollector(repo);
 *
 *   // After any post operation (create, edit, factiii change, upload):
 *   await collector.snapshotAndHash(postId);
 *
 *   // For blobs (file content verification):
 *   await collector.addBlob(blob);
 *
 *   syncHashesAsync(collector.getHashes());
 */
export class HashCollector {
  private hashes: HashRecord[] = [];
  private repo: DatabaseRepository | null;

  constructor(repo: DatabaseRepository | null = null) {
    this.repo = repo;
  }

  /**
   * Get all collected hash records
   */
  getHashes(): HashRecord[] {
    return this.hashes;
  }

  /**
   * Check if any hashes have been collected
   */
  hasHashes(): boolean {
    return this.hashes.length > 0;
  }

  /**
   * Clear collected hashes
   */
  clear(): void {
    this.hashes = [];
  }

  /**
   * Add a post snapshot hash.
   * The snapshot contains comprehensive data including post, uploads,
   * factiiis, space times, and previous snapshot hashes.
   */
  addPostSnapshot(snapshotId: string, hash: string): this {
    this.hashes.push({
      id: snapshotId,
      tableName: 'private_post_snapshot',
      hash
    });
    return this;
  }

  /**
   * Add a blob hash (includes file content).
   * Blob hashing remains separate from post snapshots.
   * Post snapshots include upload metadata (id, key, type) but NOT file content.
   */
  async addBlob(record: BlobInput): Promise<this> {
    const data = {
      name: record.name,
      content: record.content,
      createdAt:
        record.createdAt instanceof Date
          ? record.createdAt.getTime()
          : record.createdAt
    };
    this.hashes.push({
      id: record.id,
      tableName: 'blob',
      hash: await computeHash(JSON.stringify(data))
    });
    return this;
  }

  /**
   * Create snapshot of current state and compute its hash.
   * Used AFTER any operation (create, edit) to capture and hash the resulting state.
   *
   * Why we can't use "current state" without a snapshot:
   * - Hash requires a consistent `createdAt` timestamp
   * - Current state has no stable timestamp (post.createdAt never changes)
   * - Each snapshot gets its own `createdAt`
   * - This preserves WHEN each version was created for edit timing
   *
   * @returns The snapshot ID and hash, or null if failed
   */
  async snapshotAndHash(
    postId: string
  ): Promise<{ snapshotId: string; hash: string } | null> {
    if (!this.repo) {
      console.warn(
        '[HashCollector] snapshotAndHash: No repository provided, skipping'
      );
      return null;
    }

    try {
      const result = await this.repo.createAndStoreSnapshot(postId);
      if (result) {
        this.addPostSnapshot(result.snapshotId, result.hash);
      }
      return result;
    } catch (error) {
      console.warn(
        `[HashCollector] snapshotAndHash: Error for post ${postId}:`,
        error
      );
      return null;
    }
  }
}
